const STORAGE_KEY = 'velvera-attribution-v1';
const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function sanitizeTagValue(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

function normalizeAttribution(input) {
  const normalized = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = typeof input[key] === 'string' ? sanitizeTagValue(input[key]) : '';
    if (value) normalized[key] = value;
  }

  if (typeof input.referrer_host === 'string') {
    const referrerHost = sanitizeTagValue(input.referrer_host);
    if (referrerHost) normalized.referrer_host = referrerHost;
  }

  return normalized;
}

function readStoredAttribution() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return normalizeAttribution(JSON.parse(raw));
  } catch {
    return {};
  }
}

function readCurrentAttribution() {
  const url = new URL(window.location.href);
  const current = {};

  for (const key of ATTRIBUTION_KEYS) {
    const value = url.searchParams.get(key);
    if (value) current[key] = value;
  }

  if (!current.utm_source && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      if (referrerUrl.host && referrerUrl.host !== window.location.host) {
        current.referrer_host = referrerUrl.host;
      }
    } catch {
      // Ignore malformed referrers.
    }
  }

  return normalizeAttribution(current);
}

function persistAttribution(attribution) {
  if (!Object.keys(attribution).length) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Ignore storage failures.
  }
}

function getEffectiveAttribution() {
  const stored = readStoredAttribution();
  const current = readCurrentAttribution();

  if (Object.keys(current).length) {
    const merged = { ...stored, ...current };
    persistAttribution(merged);
    return merged;
  }

  return stored;
}

function buildAttributionTags(attribution) {
  const tags = [];

  for (const key of ATTRIBUTION_KEYS) {
    if (attribution[key]) tags.push(`${key}_${attribution[key]}`);
  }

  if (attribution.referrer_host) {
    tags.push(`referrer_${attribution.referrer_host}`);
  }

  return tags;
}

function getEntryRouteTag(pathname = window.location.pathname) {
  const routeParts = pathname
    .split('/')
    .filter(Boolean)
    .map((part) => sanitizeTagValue(part))
    .filter(Boolean);

  if (!routeParts.length) return 'entry_home';
  if (routeParts[0] === 'products' && routeParts[1]) return `entry_product_${routeParts[1]}`;
  if (routeParts[0] === 'collections' && routeParts[1]) return `entry_collection_${routeParts[1]}`;
  if (routeParts[0] === 'pages' && routeParts[1]) return `entry_page_${routeParts[1]}`;
  if (routeParts[0] === 'blogs' && routeParts[1] && routeParts[2]) return `entry_article_${routeParts[2]}`;
  if (routeParts[0] === 'blogs' && routeParts[1]) return `entry_blog_${routeParts[1]}`;

  return `entry_route_${routeParts.join('_')}`;
}

function getPageContextTag() {
  const template = document.querySelector('#MainContent')?.dataset.template ?? '';
  const pathname = window.location.pathname;

  if (template.includes('product') || pathname.startsWith('/products/')) return 'landing_product';
  if (template.includes('collection') || pathname.startsWith('/collections/')) return 'landing_collection';
  if (template.includes('search') || pathname.startsWith('/search')) return 'landing_search';
  if (template.includes('article') || template.includes('blog') || pathname.startsWith('/blogs/')) return 'landing_content';
  if (template.includes('page.quiz') || pathname.includes('find-my-perfect-match') || pathname.includes('/quiz')) return 'landing_quiz';
  if (template.includes('contact') || pathname.startsWith('/pages/contact')) return 'landing_contact';
  if (template.includes('index') || pathname === '/') return 'landing_homepage';
  if (template.includes('page') || pathname.startsWith('/pages/')) return 'landing_page';

  return '';
}

function getFormContextTags(form) {
  const tags = [];
  const customContext = form.querySelector('input[name="contact[signup_context]"]')?.value;

  if (customContext) {
    const sanitizedContext = sanitizeTagValue(customContext);
    if (sanitizedContext) tags.push(`capture_${sanitizedContext}`);
  }

  if (form.closest('.shopify-section-footer, footer, #shopify-section-footer-group')) {
    tags.push('form_footer');
  }

  if (form.closest('#MainContent')) {
    const pageTag = getPageContextTag();

    if (pageTag === 'landing_homepage') tags.push('form_homepage_capture');
    if (pageTag === 'landing_product') tags.push('form_product_capture');
    if (pageTag === 'landing_collection') tags.push('form_collection_capture');
    if (pageTag === 'landing_quiz') tags.push('form_quiz_capture');
  }

  if (form.closest('.sticky-add-to-cart')) {
    tags.push('form_sticky_add_to_cart');
  }

  return tags;
}

function mergeTags(baseTags, attributionTags) {
  const merged = [...baseTags, ...attributionTags].filter(Boolean);
  return [...new Set(merged)];
}

function inheritUtmParamsIntoLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return;

  const href = link.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

  const currentUrl = new URL(window.location.href);
  const supportUrl = new URL(href, currentUrl);

  if (supportUrl.origin !== currentUrl.origin) return;

  for (const paramName of ATTRIBUTION_KEYS) {
    const currentValue = currentUrl.searchParams.get(paramName);
    if (currentValue && !supportUrl.searchParams.get(paramName)) {
      supportUrl.searchParams.set(paramName, currentValue);
    }
  }

  link.setAttribute('href', `${supportUrl.pathname}${supportUrl.search}${supportUrl.hash}`);
}

function prepareSignupForm(form) {
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.querySelector('input[name="contact[email]"]')) return;

  const attribution = getEffectiveAttribution();
  const attributionTags = buildAttributionTags(attribution);
  const contextTags = [getPageContextTag(), getEntryRouteTag(), ...getFormContextTags(form)].filter(Boolean);
  if (!attributionTags.length && !contextTags.length) return;

  let tagField = form.querySelector('input[name="contact[tags]"]');

  if (!tagField) {
    tagField = document.createElement('input');
    tagField.type = 'hidden';
    tagField.name = 'contact[tags]';
    form.prepend(tagField);
  }

  const baseTags = tagField.dataset.baseTags ?? tagField.value ?? '';
  tagField.dataset.baseTags = baseTags;

  const baseTagList = baseTags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  tagField.value = mergeTags(baseTagList, [...contextTags, ...attributionTags]).join(', ');
}

function prepareSignupForms(root = document) {
  const forms = root.querySelectorAll('form');
  forms.forEach((form) => prepareSignupForm(form));
}

function getSignupSupportLinks(root = document) {
  const links = new Set(root.querySelectorAll('.email-signup__support-link'));

  root.querySelectorAll('form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.querySelector('input[name="contact[email]"]')) return;
    form.querySelectorAll('a[href]').forEach((link) => links.add(link));
  });

  return [...links];
}

function getTrackedCtaLinks(root = document) {
  const selectors = [
    '.sticky-add-to-cart__support-link',
    '.velvera-sticky-cta a',
    '.velvera-home-faq a',
    '.velvera-home-route-summary a',
    '.velvera-home-roadmap a',
    '.velvera-hero-proof a',
    '.velvera-home-shortcuts a',
    '.velvera-home-proof-guide a',
    '.velvera-home-brand-signature a',
    '.velvera-shade-family a',
    '.velvera-home-method-guide a',
    '.velvera-home-method-snapshot a',
    '.velvera-social-proof a',
    '.velvera-shade-guide a',
    '.lead-capture-form__support-actions a',
    '.velvera-search-mobile-bar a',
    '.velvera-search-guidance a',
    '.velvera-search-intent-panel a',
    '.velvera-search-shortcuts a',
    '.velvera-search-recovery a',
    '.velvera-collection-context a',
    '.velvera-collection-proof a',
    '.velvera-collection-mobile-bar a',
    '.velvera-product-review-paths a',
    '.velvera-product-decision-strip a',
    '.velvera-product-match__actions a',
    '.velvera-product-selection-summary__actions a',
    '.velvera-product-support-steps a',
    '.velvera-lead-expectations a',
    '.velvera-quiz-start a',
    '.velvera-quiz-decision-paths a',
    '.velvera-quiz-process a',
    '.velvera-quiz-aftercare a',
    '.velvera-quiz-shop-paths a',
    '.velvera-quiz-help a',
  ];
  const links = new Set();

  selectors.forEach((selector) => {
    root.querySelectorAll(selector).forEach((link) => links.add(link));
  });

  return [...links];
}

function getFooterSupportOverrides(pageTag) {
  if (pageTag === 'landing_product') {
    return {
      primary: {
        href: '#velvera-product-reviews',
        label: 'Read product reviews',
        ariaLabel: 'Read product reviews from footer guidance',
      },
      secondary: {
        href: '#velvera-product-faq',
        label: 'Open product FAQ',
        ariaLabel: 'Open product FAQ from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_collection') {
    return {
      primary: {
        href: '#velvera-collection-proof',
        label: 'Read collection proof',
        ariaLabel: 'Read collection proof from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_search') {
    return {
      primary: {
        href: '/?utm_source=footer&utm_medium=signup_support&utm_campaign=compare_methods#velvera-home-method-guide',
        label: 'Compare methods',
        ariaLabel: 'Compare extension methods from footer guidance',
      },
      secondary: {
        href: '/pages/how-to-match-your-hair-extension-shade?utm_source=footer&utm_medium=signup_support&utm_campaign=shade_guide',
        label: 'Read shade guide',
        ariaLabel: 'Read the shade guide from footer guidance',
      },
      tertiary: {
        href: '/pages/find-my-perfect-match?utm_source=footer&utm_medium=signup_support&utm_campaign=find_my_match_after_search',
        label: 'Start Find My Match',
        ariaLabel: 'Start Find My Match from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_content') {
    return {
      primary: {
        href: '/pages/find-my-perfect-match?utm_source=footer&utm_medium=signup_support&utm_campaign=find_my_match_after_guide',
        label: 'Start Find My Match',
        ariaLabel: 'Start Find My Match from footer guidance',
      },
      secondary: {
        href: '/?utm_source=footer&utm_medium=signup_support&utm_campaign=compare_methods_after_guide#velvera-home-method-guide',
        label: 'Compare methods',
        ariaLabel: 'Compare extension methods from footer guidance',
      },
      tertiary: {
        href: '/pages/how-to-match-your-hair-extension-shade?utm_source=footer&utm_medium=signup_support&utm_campaign=shade_guide_after_guide',
        label: 'Read shade guide',
        ariaLabel: 'Read the shade guide from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_contact') {
    return {
      primary: {
        href: '/pages/find-my-perfect-match?utm_source=footer&utm_medium=signup_support&utm_campaign=find_my_match_before_contact',
        label: 'Start Find My Match',
        ariaLabel: 'Start Find My Match from footer guidance',
      },
      secondary: {
        href: '/?utm_source=footer&utm_medium=signup_support&utm_campaign=read_reviews_before_contact#velvera-homepage-reviews',
        label: 'Read customer reviews',
        ariaLabel: 'Read customer reviews from footer guidance',
      },
      tertiary: {
        href: '/pages/how-to-match-your-hair-extension-shade?utm_source=footer&utm_medium=signup_support&utm_campaign=shade_guide_before_contact',
        label: 'Read shade guide',
        ariaLabel: 'Read the shade guide from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_quiz') {
    return {
      primary: {
        href: '#velvera-quiz-app',
        label: 'Restart the quiz',
        ariaLabel: 'Restart the quiz from footer guidance',
      },
      secondary: {
        href: '#velvera-quiz-email-fallback',
        label: 'Use email fallback',
        ariaLabel: 'Use the quiz email fallback from footer guidance',
      },
      tertiary: {
        href: '#velvera-quiz-aftercare-title',
        label: 'Review next steps',
        ariaLabel: 'Review quiz next steps from footer guidance',
      },
    };
  }

  if (pageTag === 'landing_homepage') {
    return {
      primary: {
        href: '#velvera-homepage-reviews',
        label: 'Read blend reviews',
        ariaLabel: 'Read blend reviews from footer guidance',
      },
    };
  }

  return {};
}

function applySupportLinkDefaults(link) {
  if (!(link instanceof HTMLAnchorElement)) return;

  const defaultHref = link.dataset.defaultHref;
  const defaultLabel = link.dataset.defaultLabel;
  const defaultAriaLabel = link.dataset.defaultAriaLabel;

  if (defaultHref) link.setAttribute('href', defaultHref);
  if (defaultAriaLabel) {
    link.setAttribute('aria-label', defaultAriaLabel);
  } else {
    link.removeAttribute('aria-label');
  }
  if (defaultLabel) link.textContent = defaultLabel;
}

function contextualizeFooterSignupLinks(root = document) {
  const pageTag = getPageContextTag();
  const overrides = getFooterSupportOverrides(pageTag);

  root.querySelectorAll('.email-signup__support-links[data-signup-context="footer_guidance"]').forEach((group) => {
    group.querySelectorAll('.email-signup__support-link').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      applySupportLinkDefaults(link);

      const override = overrides[link.dataset.supportSlot];
      if (!override) return;

      link.setAttribute('href', override.href);
      link.textContent = override.label;
      link.setAttribute('aria-label', override.ariaLabel);
    });
  });
}

function contextualizeMenuDrawerLinks(root = document) {
  const pageTag = getPageContextTag();

  root.querySelectorAll('[data-velvera-contextual-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const title = link.querySelector('strong');
    const copy = link.querySelector('span');
    const linkType = link.dataset.velveraContextualLink;

    if (linkType === 'reviews') {
      if (pageTag === 'landing_product') {
        link.setAttribute('href', '#velvera-product-reviews');
        link.setAttribute('aria-label', 'Read product reviews for blend, comfort, and softness proof');
        if (title) title.textContent = 'Read product reviews';
        if (copy) copy.textContent = 'Check product-page review proof on blend, softness, and comfort before you open more pages.';
        return;
      }

      if (pageTag === 'landing_collection') {
        link.setAttribute('href', '#velvera-collection-proof');
        link.setAttribute('aria-label', 'Read collection proof before opening more products');
        if (title) title.textContent = 'Read collection proof';
        if (copy) copy.textContent = 'Use collection proof first when you want faster reassurance before opening more product pages.';
        return;
      }
    }

    if (linkType === 'faq' && pageTag === 'landing_product') {
      link.setAttribute('href', '#velvera-product-faq');
      link.setAttribute('aria-label', 'Open product frequently asked questions for last-step answers');
      if (title) title.textContent = 'Open product FAQ';
      if (copy) copy.textContent = 'Use the product FAQ when shipping, returns, or final matching details are the last blocker.';
    }
  });
}

function getCartJourneyContext(root = document) {
  const journey = root.querySelector('[data-velvera-cart-journey]');
  if (!(journey instanceof HTMLElement)) return { route: '', itemCount: 0 };

  return {
    route: String(journey.dataset.cartRoute || ''),
    itemCount: Number(journey.dataset.cartItemCount || '0'),
  };
}

function applyLeadCaptureDefaults(link) {
  if (!(link instanceof HTMLAnchorElement)) return;

  const defaultHref = link.dataset.defaultHref;
  const defaultLabel = link.dataset.defaultLabel;

  if (defaultHref) link.setAttribute('href', defaultHref);
  if (defaultLabel) link.textContent = defaultLabel;
}

function getLeadCaptureOverrides(signupContext, cartContext) {
  if (signupContext === 'cart_capture') {
    if (cartContext.route === 'clip_in') {
      return {
        primary: {
          href: '/collections/clip-in-extensions?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=clipin_beginners#velvera-clipin-beginner-guide',
          label: 'Read beginner clip-in notes',
        },
        secondary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=clipin_reviews#velvera-homepage-reviews',
          label: 'Read clip-in reviews',
        },
      };
    }

    if (cartContext.route === 'tape_in') {
      return {
        primary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=compare_methods#velvera-home-method-guide',
          label: 'Compare extension methods',
        },
        secondary: {
          href: '/pages/find-my-perfect-match?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=tapein_find_my_match',
          label: 'Use Find My Match',
        },
      };
    }

    if (cartContext.route === 'ponytail') {
      return {
        primary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=shade_checks#velvera-homepage-shade-guide',
          label: 'Review shade checks',
        },
        secondary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=ponytail_reviews#velvera-homepage-reviews',
          label: 'Read customer reviews',
        },
      };
    }

    if (cartContext.route === 'bundle') {
      return {
        primary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=bundle_reviews#velvera-homepage-reviews',
          label: 'Read bundle-stage reviews',
        },
        secondary: {
          href: '/?utm_source=cart&utm_medium=lead_capture_support&utm_campaign=bundle_compare_methods#velvera-home-method-guide',
          label: 'Compare methods',
        },
      };
    }
  }

  if (signupContext === 'empty_cart_capture') {
    return {
      primary: {
        href: '/pages/find-my-perfect-match?utm_source=empty_cart&utm_medium=lead_capture_support&utm_campaign=find_my_match',
        label: 'Start Find My Match',
      },
      secondary: {
        href: '/?utm_source=empty_cart&utm_medium=lead_capture_support&utm_campaign=read_reviews#velvera-homepage-reviews',
        label: 'Read customer reviews',
      },
      tertiary: {
        href: '/?utm_source=empty_cart&utm_medium=lead_capture_support&utm_campaign=shade_checks#velvera-homepage-shade-guide',
        label: 'Review shade checks',
      },
    };
  }

  return {};
}

function contextualizeLeadCaptureLinks(root = document) {
  const cartContext = getCartJourneyContext(root);

  root.querySelectorAll('.lead-capture-form__support[data-signup-context]').forEach((group) => {
    const signupContext = String(group.dataset.signupContext || '');
    const overrides = getLeadCaptureOverrides(signupContext, cartContext);

    group.querySelectorAll('.lead-capture-form__support-actions a').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      applyLeadCaptureDefaults(link);

      const override = overrides[link.dataset.supportSlot];
      if (!override) return;

      link.setAttribute('href', override.href);
      link.textContent = override.label;
    });
  });
}

function prepareSupportLinks(root = document) {
  contextualizeFooterSignupLinks(root);
  contextualizeLeadCaptureLinks(root);
  contextualizeMenuDrawerLinks(root);
  const links = new Set([...getSignupSupportLinks(root), ...getTrackedCtaLinks(root)]);
  links.forEach((link) => inheritUtmParamsIntoLink(link));
}

function initUtmAttribution() {
  getEffectiveAttribution();
  prepareSignupForms();
  prepareSupportLinks();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches?.('form')) prepareSignupForm(node);
        if (node.matches?.('.email-signup__support-link')) inheritUtmParamsIntoLink(node);
        prepareSignupForms(node);
        prepareSupportLinks(node);
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUtmAttribution, { once: true });
} else {
  initUtmAttribution();
}
